import React, { useContext, useEffect, useRef, useState } from 'react'

import { PopoverElementKind } from '../floor-plan-popover/hooks/useHoverCardPosition'
import { DEFAULT_ICON_SIZE } from '../hooks/useFloorChart'
import { useDeviceConnections } from './useDeviceConnections'
import { useFloorData } from './useFloorData'
import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import { ApolloQueryResult } from '@apollo/client'
import { useDisclosure } from '@chakra-ui/react'

import { DragEventI } from '@/components/ui'
import { useTailgateMixpanelEvents } from '@/features/tailgate-grid'
import { FacilityDevicesByFloorsQuery } from '@/graphql/generated/operations'
import {
  DeviceType,
  Exact,
  UpdateTailgateEnabledInput,
} from '@/graphql/generated/schemas'
import { selectMe } from '@/redux/me/meSlice'
import { AppState } from '@/redux/store'
import {
  openDeviceDrawer,
  saveFloorPlanIconSize,
  selectFacilityDrawerOpen,
  selectFloorPlanIconSize,
  setDevice,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, mixpanel, trackEvent } from '@/utils/analytics'
import { isDeviceDrawerSupported } from '@/utils/devices'
import { SEARCH_DEBOUNCE_TIME } from '@/utils/forms/validation'

import { FLOOR_PLAN_DEVICES_ANALYTICS } from '../constants/constants'
import {
  AssociateDeviceI,
  ConnectionI,
  DeviceHoverItemI,
  DeviceI,
  FloorI,
  Point,
  Size,
  TailgateHoverItemI,
} from '../types/types'
import { getTailgateConnectionElementId } from '../utils/chart'

export type RefetchDevicesFunction = (
  variables?: Partial<
    Exact<{
      floorId: string
      types?: DeviceType | DeviceType[]
    }>
  >
) => Promise<ApolloQueryResult<FacilityDevicesByFloorsQuery>>

type FloorPlanContextType = {
  floorId: string
  facilityId: string
  selectedDevice: DeviceI
  devices: DeviceI[]
  checkpointDevices: DeviceI[]
  imageUrl: string
  isFloorPlanKnownEmpty: boolean
  isFloorDataLoading: boolean
  isRemoveCheckPointOpen: boolean
  isAssociateDeviceOpen: boolean
  isTailgateGridOpen: boolean
  isTailgateHovering: boolean
  tailgateHoverItem: TailgateHoverItemI
  isDeviceHovering: boolean
  deviceHoverItem: DeviceHoverItemI
  forceCloseSignal: number
  devicePopoverOpenSignal: number
  associateDeviceValues: AssociateDeviceI
  shouldShowAssociatedDevices: boolean
  highlightedDeviceIds: string[]
  tourStopDeviceIds: string[]
  isConnectionsLoading: boolean
  isEnableTailgateLoading: boolean
  isRemovingDevice: boolean
  floorPlanIconSize: number
  nameLikeFilter: string
  onIconResize: (newSize: number) => void
  setNameLikeFilter: (filter: string) => void
  connections: ConnectionI[]
  connectDevices: (
    camera: DeviceI,
    door: DeviceI,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  disconnectDevices: (
    cameraId: string,
    doorId: string,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  enableTailgate: (
    input: UpdateTailgateEnabledInput,
    refetchDevices: RefetchDevicesFunction
  ) => Promise<void>
  getConnection: (cameraId: string, doorId: string) => ConnectionI
  setHighlightedDeviceIds: React.Dispatch<React.SetStateAction<string[]>>
  setTourStopDeviceIds: React.Dispatch<React.SetStateAction<string[]>>
  setShouldShowAssociatedDevices: React.Dispatch<React.SetStateAction<boolean>>
  onDeviceClicked: (device: DeviceI) => void
  onDeviceDragStart: (device: DeviceI) => void
  onFloorPlanContainerClick: () => void
  onMoveCheckPointClicked: (deviceId: string) => void
  onRemoveCheckpoint: (device: DeviceI) => void
  onDeviceDragged: (device: DeviceI, isPositionValid: boolean) => void
  onDeviceDragEnd: (device: DeviceI, isPositionValid: boolean) => void
  onTileDragStart: (event: DragEventI) => void
  handleDeviceDropped: (
    position: Point,
    isValidPosition: boolean,
    mapImageSize?: Size
  ) => boolean
  onDeviceHover: (
    d: DeviceI & ConnectionI,
    id: string,
    kind: PopoverElementKind
  ) => void
  onDeviceHoverOut: () => void
  forceCloseHoverCards: () => void
  onRemoveDevicePlacement: (deviceId: string) => Promise<boolean>
  onConnectionButtonClicked: (con: ConnectionI) => void
  onRemoveCheckPointClose: () => void
  onAssociateDeviceClose: () => void
  onTailgateGridOpen: () => void
  onTailgateGridClose: () => void
  refetchDevices: RefetchDevicesFunction
  trackTailgateEvent: (name: string) => void
}

export const FloorPlanContext = React.createContext<FloorPlanContextType>(null)

interface FloorPlanProviderIProps {
  floorId: string
  floors: FloorI[]
  facilityId: string
  children: React.ReactNode
}

export const FloorPlanProvider = ({
  floorId,
  floors,
  facilityId,
  children,
}: FloorPlanProviderIProps) => {
  const dispatch = useDispatch()
  const me = useSelector(selectMe)
  // Keep as old Edit Mode check!
  const isEditMode = useIsEditMode()
  const drawerOpen = useSelector(selectFacilityDrawerOpen)
  const isGuardTour =
    drawerOpen === 'checkpoints' || drawerOpen === 'tour-plans'
  const deviceTypes = isGuardTour
    ? [DeviceType.Checkpoint]
    : [DeviceType.Alarm, DeviceType.Camera, DeviceType.Door]

  const [selectedDevice, setSelectedDevice] = useState(null)
  const [draggedData, setDraggedData] = useState(null)
  const [isTailgateHovering, setIsTailgateHovering] = useState(false)
  const [tailgateHoverItem, setTailgateHoverItem] =
    useState<TailgateHoverItemI>(null)
  const [isDeviceHovering, setIsDeviceHovering] = useState(false)
  const [deviceHoverItem, setDeviceHoverItem] = useState<DeviceHoverItemI>(null)
  const deviceHoverItemRef = useRef<DeviceHoverItemI>(null)
  deviceHoverItemRef.current = deviceHoverItem
  const [forceCloseSignal, setForceCloseSignal] = useState(0)
  const [devicePopoverOpenSignal, setDevicePopoverOpenSignal] = useState(0)

  const [associateDeviceValues, setAssociateDeviceValues] =
    useState<AssociateDeviceI>(null)
  const [highlightedDeviceIds, setHighlightedDeviceIds] =
    useState<string[]>(null)
  const [tourStopDeviceIds, setTourStopDeviceIds] = useState<string[]>(null)
  const [shouldShowAssociatedDevices, setShouldShowAssociatedDevices] =
    useState(true)
  const [nameLikeFilter, setNameLikeFilter] = useState<string>('')
  const [debouncedNameLikeFilter] = useDebounce(
    nameLikeFilter,
    SEARCH_DEBOUNCE_TIME
  )

  useEffect(() => {
    if (drawerOpen !== 'tour-plans' && tourStopDeviceIds !== null) {
      setTourStopDeviceIds(null)
    }
  }, [drawerOpen, tourStopDeviceIds])

  // Track device search events
  useEffect(() => {
    if (debouncedNameLikeFilter) {
      trackEvent('Floor Plan - Device Search', {
        search_term: debouncedNameLikeFilter,
        show_associated_devices: shouldShowAssociatedDevices,
        facility_id: facilityId,
        floor_id: floorId,
      })
    }
  }, [
    debouncedNameLikeFilter,
    shouldShowAssociatedDevices,
    facilityId,
    floorId,
  ])

  const iconSizeFromState: number = useSelector((state: AppState) =>
    selectFloorPlanIconSize(state, floorId)
  )

  const [floorPlanIconSize, setFloorPlanIconSize] = useState(
    iconSizeFromState || DEFAULT_ICON_SIZE
  )

  useEffect(() => {
    setFloorPlanIconSize(iconSizeFromState || DEFAULT_ICON_SIZE)
  }, [iconSizeFromState])

  const editModeRef = useRef(isEditMode)
  editModeRef.current = isEditMode

  // Set whenever a marker is hovered, cleared by forceCloseHoverCards. Lets
  // a forced close (pan/zoom, floor switch, panel collapse, modal open/close)
  // skip work — and the re-render it would cause — when nothing could
  // possibly be open.
  const hasHoveredSinceForceCloseRef = useRef(false)

  const {
    isOpen: isRemoveCheckPointOpen,
    onOpen: onRemoveCheckPointOpen,
    onClose: onRemoveCheckPointClose,
  } = useDisclosure()

  const {
    isOpen: isAssociateDeviceOpen,
    onOpen: onAssociateDeviceOpen,
    onClose: onAssociateDeviceClose,
  } = useDisclosure()

  const {
    isOpen: isTailgateGridOpen,
    onOpen: onTailgateGridOpen,
    onClose: onTailgateGridClose,
  } = useDisclosure()

  const {
    devices,
    checkpointDevices,
    imageUrl,
    isFloorPlanKnownEmpty,
    loading: isFloorDataLoading,
    isRemovingDevice: getIsRemovingDevice,
    onDeviceClicked: onFCDecivceClicked,
    onDeviceDragStart: onFCDeviceDragStart,
    onFloorPlanContainerClick,
    onMoveCheckPointClicked,
    onDeviceUpdated,
    onDeviceReleased,
    onDeviceDropped,
    onRemoveDevicePlacement: removeDevicePlacement,
    refetchDevices,
  } = useFloorData(
    floorId,
    floors,
    facilityId,
    isEditMode,
    deviceTypes,
    debouncedNameLikeFilter,
    shouldShowAssociatedDevices
  )
  const isRemovingDevice = getIsRemovingDevice(deviceHoverItem?.deviceId)

  const {
    isLoading: isConnectionsLoading,
    isEnableTailgateLoading,
    connections,
    connectDevices,
    disconnectDevices,
    enableTailgate,
    getConnection,
  } = useDeviceConnections(devices)

  const { trackTailgateEvent } = useTailgateMixpanelEvents({
    facilityId,
    floorId,
    cameraId: associateDeviceValues?.cameraId,
    doorId: associateDeviceValues?.doorId,
  })

  const getDevice = (id: string): DeviceI => {
    return devices.find((d) => d.id === id)
  }

  const onDeviceClicked = (data: DeviceI) => {
    if (!editModeRef.current) {
      handleOpenDeviceDrawer(data.id, data.deviceType)
      return
    }
    if (data.deviceType === DeviceType.Checkpoint) {
      setHighlightedDeviceIds([data.id])
    }
    setHighlightedDeviceIds([data.id])
    setSelectedDevice(data)
    onFCDecivceClicked(data.id)

    if (data.deviceType !== DeviceType.Checkpoint) {
      hasHoveredSinceForceCloseRef.current = true
      setDeviceHoverItem({
        elementId: data.id,
        deviceId: data.id,
        deviceName: data.deviceName,
        deviceType: data.deviceType,
      })
      setIsDeviceHovering(true)
      setIsTailgateHovering(false)
      setDevicePopoverOpenSignal((n) => n + 1)
    }
  }

  const handleOpenDeviceDrawer = (deviceId: string, deviceType: DeviceType) => {
    // Checkpoints are the reachable case here — they're excluded from the
    // devices table but do render on the floor plan, and have no drawer.
    if (!isDeviceDrawerSupported(deviceType)) return
    dispatch(setDevice({ id: deviceId, type: deviceType }))
    dispatch(openDeviceDrawer())
  }

  const onRemoveCheckpoint = (c: DeviceI) => {
    setSelectedDevice(c)
    onRemoveCheckPointOpen()
    mixpanel.track('Button Press: Map Checkpoint - Remove from floor', {
      checkpoint_id: c.checkpoint.id,
      facility_id: c.checkpoint.facility.id,
      floor_id: c.checkpoint.floor.id,
      role: me.roleName,
    })
  }

  const onDeviceDragged = (data: DeviceI, isPositionValid: boolean) => {
    handleDeviceUpdated(data, isPositionValid)
  }

  const onDeviceDragStart = (data: DeviceI) => {
    if (!editModeRef.current) return
    onFCDeviceDragStart(data)
  }

  const onDeviceDragEnd = (data: DeviceI, isPositionValid: boolean) => {
    handleDeviceReleased(data, isPositionValid)
  }

  const handleDeviceUpdated = (data: DeviceI, isPositionValid: boolean) => {
    if (!editModeRef.current) {
      return
    }
    onDeviceUpdated(data, isPositionValid)
  }

  const handleDeviceReleased = (data: DeviceI, isPositionValid: boolean) => {
    if (!editModeRef.current) return
    handleDeviceUpdated(data, isPositionValid)

    onDeviceReleased(data, isPositionValid)
  }

  // Called when drag from panel
  const onTileDragStart = (event: DragEventI) => {
    if (!editModeRef.current) return
    setDraggedData(event)
  }

  // Called when dropped from panel
  const handleDeviceDropped = (
    position: Point,
    isValidPosition: boolean,
    mapImageSize?: Size
  ) => {
    if (!editModeRef.current || !draggedData) return
    if (isValidPosition) {
      onDeviceDropped(draggedData.data.id, position, {
        iconSize: floorPlanIconSize,
        mapImageSize,
      })
    }
    setDraggedData(null)
    return false
  }

  const onDeviceHover = (
    data: DeviceI & ConnectionI,
    elementId: string,
    kind: PopoverElementKind
  ) => {
    hasHoveredSinceForceCloseRef.current = true
    switch (kind) {
      case 'tailgate': {
        const camera = getDevice(data?.cameraId)
        const device = getDevice(data?.deviceId)
        setTailgateHoverItem({
          elementId: getTailgateConnectionElementId(data),
          connection: data,
          cameraName: camera.deviceName,
          doorName: device.deviceName,
        })
        setIsTailgateHovering(true)
        // Only one hover card at a time.
        setIsDeviceHovering(false)
        break
      }

      default:
        setDeviceHoverItem({
          elementId,
          deviceId: data.id,
          deviceName: data.deviceName,
          deviceType: data.deviceType,
        })
        setIsDeviceHovering(true)
        // Only one hover card at a time.
        setIsTailgateHovering(false)
        break
    }
  }

  const onIconResize = (newSize: number) => {
    setFloorPlanIconSize(newSize)

    // TODO - Look at deboucing this? If somebody is on a lower powered device
    //  and drags the slider up and down a ton it might cause issues saving
    //  each little step
    dispatch(
      saveFloorPlanIconSize({
        floorId: floorId,
        iconSize: newSize,
      })
    )
  }

  const onDeviceHoverOut = () => {
    setIsTailgateHovering(false)
    setIsDeviceHovering(false)
  }

  // Unlike onDeviceHoverOut (ordinary marker mouseout, which still respects
  // the card's own "pointer moved onto the card" grace period), this closes
  // a hover card unconditionally — the anchor itself may now be invalid.
  // Skipped when nothing has been hovered since the last time this ran: pan
  // and zoom call this on every pointer-move frame, and bumping the signal
  // unconditionally re-renders the whole provider (and, downstream, causes
  // a full d3 update pass) even when there is nothing to close.
  const forceCloseHoverCards = () => {
    if (!hasHoveredSinceForceCloseRef.current) return
    hasHoveredSinceForceCloseRef.current = false
    onDeviceHoverOut()
    setForceCloseSignal((n) => n + 1)
  }

  const onRemoveDevicePlacement = async (deviceId: string) => {
    const isRemoved = await removeDevicePlacement(deviceId)
    if (!isRemoved) return false

    setSelectedDevice((currentDevice) =>
      currentDevice?.id === deviceId ? null : currentDevice
    )
    setHighlightedDeviceIds((currentIds) => {
      if (!currentIds?.includes(deviceId)) return currentIds
      const remainingIds = currentIds.filter((id) => id !== deviceId)
      return remainingIds.length ? remainingIds : null
    })
    trackEvent(FLOOR_PLAN_DEVICES_ANALYTICS.removePlacement, {
      device_id: deviceId,
      facility_id: facilityId,
      floor_id: floorId,
      role: me?.roleName,
    })
    if (deviceHoverItemRef.current?.deviceId === deviceId) {
      forceCloseHoverCards()
    }
    return true
  }

  // A floor switch or modal open/close can move or unmount a hover card's
  // marker without ever firing a mouseout, so clear it proactively.
  useEffect(() => {
    forceCloseHoverCards()
  }, [
    floorId,
    isRemoveCheckPointOpen,
    isAssociateDeviceOpen,
    isTailgateGridOpen,
  ])

  const onConnectionButtonClicked = (con: ConnectionI) => {
    const camera = getDevice(con.cameraId)
    const device = getDevice(con.deviceId)
    mixpanel.track(`${BUTTON_PRESS} Device Association`, {
      facility_id: facilityId,
      floor_id: floorId,
      camera_id: con.cameraId,
      door_id: con.deviceId,
      role: me.roleName,
    })

    if (editModeRef.current) {
      setAssociateDeviceValues({
        cameraId: con.cameraId,
        doorId: con.deviceId,
        cameraName: camera.deviceName,
        doorName: device.deviceName,
        deviceType: con.deviceType,
      })
      onAssociateDeviceOpen()
    }
  }

  return (
    <FloorPlanContext.Provider
      value={{
        floorId,
        facilityId,
        devices,
        checkpointDevices,
        selectedDevice,
        imageUrl,
        isFloorPlanKnownEmpty,
        isFloorDataLoading,
        isRemoveCheckPointOpen,
        isTailgateHovering,
        tailgateHoverItem,
        isDeviceHovering,
        deviceHoverItem,
        forceCloseSignal,
        devicePopoverOpenSignal,
        associateDeviceValues,
        shouldShowAssociatedDevices,
        isAssociateDeviceOpen,
        isTailgateGridOpen,
        highlightedDeviceIds,
        tourStopDeviceIds,
        isConnectionsLoading,
        isEnableTailgateLoading,
        isRemovingDevice,
        connections,
        floorPlanIconSize,
        nameLikeFilter,
        onIconResize,
        setNameLikeFilter,
        connectDevices,
        disconnectDevices,
        enableTailgate,
        getConnection,
        setHighlightedDeviceIds,
        setTourStopDeviceIds,
        setShouldShowAssociatedDevices,
        onDeviceClicked,
        onDeviceDragStart,
        onFloorPlanContainerClick,
        onMoveCheckPointClicked,
        onRemoveCheckpoint,
        onRemoveCheckPointClose,
        onDeviceDragged,
        onDeviceDragEnd,
        onTileDragStart,
        handleDeviceDropped,
        onDeviceHover,
        onDeviceHoverOut,
        forceCloseHoverCards,
        onRemoveDevicePlacement,
        onConnectionButtonClicked,
        onAssociateDeviceClose,
        onTailgateGridOpen,
        onTailgateGridClose,
        refetchDevices,
        trackTailgateEvent,
      }}
    >
      {children}
    </FloorPlanContext.Provider>
  )
}

export const useFloorPlan = () => {
  return useContext(FloorPlanContext)
}
